// A venue's counters, and the drawers running at them (ADR-0032; spec/13 §10, §13; F-OPS-01, F-OPS-04).
//
// A **counter** is a place a cash drawer is kept — "Front desk", "Bar". A shift (drawer custody, ADR-0029) starts at
// one of them, picked from this list, so a venue's drawers are named the same way every day. Settings → Counters keeps
// the list: add, rename, archive and restore, a default float, and an optional link to the Clavian Counter PC there (a
// station with role `counter`, for its receipt printer and cash drawer).
//
//   GET   …/venues/{v}/counters                 the counters, each with its running drawer    shifts:own (+ shifts:read_all)
//   POST  …/venues/{v}/counters                 add a counter                                  settings:payments
//   PATCH …/venues/{v}/counters/{counterId}     rename, default float, Counter PC, archive     settings:payments
//
// The list is also the **running counters panel** on Sell and Shifts: each counter free or with a drawer open — who is
// on it, since when — and, for whom may see it, the float, the cash taken so far, the expected drawer now and a
// per-person breakdown. Every figure is read from the ledger lines the shift's close and cash timeline read (spec/06
// §2.4 invariant 6), so the panel, the timeline and the count agree.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { CounterIdSchema, MemberIdSchema, ShiftIdSchema, StationIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema, NonNegativeMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema, LocalDateSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { OTHERS_PERMISSION_EXTENSION, PERMISSION_EXTENSION } from "./audit.js";
import { SETTINGS_PAYMENTS, SHIFTS_OWN, SHIFTS_READ_ALL } from "../permissions/index.js";
/** Someone who has been on a running drawer, with what they took into it. */
export const RunningDrawerMemberSchema = z
    .object({
    memberId: MemberIdSchema,
    name: z.string().nullable(),
    joinedAt: IsoDateTimeSchema,
    /** When they left the drawer; null while on it. */
    leftAt: IsoDateTimeSchema.nullable(),
    /** The cash they took into the drawer, net of cash refunds they gave; null without the figures. */
    cash: MoneySchema.nullable(),
    /** Sales they settled at the counter into this drawer (any counter tender); null without the figures. */
    salesCount: z.int().min(0).nullable(),
})
    .openapi('RunningDrawerMember');
/**
 * A drawer running now: its shift, who has been on it and since when, and — for a member on it or one with
 * `shifts:read_all` — its float, the cash taken so far and the expected drawer (the cash timeline's running balance).
 */
export const RunningDrawerSchema = z
    .object({
    shiftId: ShiftIdSchema,
    /** The counter it runs at; null for a drawer opened at no counter of the list. */
    counterId: CounterIdSchema.nullable(),
    counterName: z.string().min(1).max(60).nullable(),
    openedAt: IsoDateTimeSchema,
    businessDay: LocalDateSchema,
    openedByMemberId: MemberIdSchema.nullable(),
    members: z.array(RunningDrawerMemberSchema),
    /** Whether the money is shown: the caller is on this drawer, or holds `shifts:read_all`. */
    figuresVisible: z.boolean(),
    openingFloat: NonNegativeMoneySchema.nullable(),
    /** Cash taken into the drawer so far, net of cash refunds. */
    cashSoFar: MoneySchema.nullable(),
    /** What should be in the drawer now: float, cash taken, pay-ins less pay-outs and drops. */
    expectedDrawer: MoneySchema.nullable(),
    /** Cash taken into it by someone not on it (before ADR-0031's rule, or a shared venue's colleague not joined). */
    otherCash: MoneySchema.nullable(),
})
    .openapi('RunningDrawer');
/** A counter: its name, default float and Counter PC, whether it is archived, and the drawer running there. */
export const CounterSchema = z
    .object({
    id: CounterIdSchema,
    venueId: z.string(),
    name: z.string().min(1).max(60),
    defaultFloat: NonNegativeMoneySchema,
    /** The Clavian Counter PC at this counter (a station with role `counter`). */
    stationId: StationIdSchema.nullable(),
    archived: z.boolean(),
    archivedAt: IsoDateTimeSchema.nullable(),
    /** What the last shift here counted at its close — what the next float is pre-filled with; null with none. */
    lastClosingCount: NonNegativeMoneySchema.nullable(),
    /** The drawer open here now; null when the counter is free. */
    running: RunningDrawerSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('Counter');
export const CounterBoardSchema = z
    .object({
    /** Active counters by name, then (with `includeArchived`) the archived ones. */
    counters: z.array(CounterSchema),
    /** Drawers open at no counter of the list, oldest first. */
    unassigned: z.array(RunningDrawerSchema),
})
    .openapi('CounterBoard');
export const ListCountersQuerySchema = z
    .object({ includeArchived: z.enum(['true', 'false']).optional() })
    .openapi('ListCountersQuery');
export const CreateCounterSchema = z
    .object({
    name: z.string().trim().min(1).max(60),
    defaultFloat: NonNegativeMoneySchema.optional(),
    stationId: StationIdSchema.optional(),
})
    .strict()
    .openapi('CreateCounterRequest');
export const UpdateCounterSchema = z
    .object({
    name: z.string().trim().min(1).max(60).optional(),
    defaultFloat: NonNegativeMoneySchema.optional(),
    /** Null unlinks the Counter PC. */
    stationId: StationIdSchema.nullable().optional(),
    /** `true` archives, `false` restores. */
    archived: z.boolean().optional(),
})
    .strict()
    .openapi('UpdateCounterRequest');
const CounterParamsSchema = VenueScopeParamsSchema.extend({ counterId: CounterIdSchema }).openapi('CounterParams');
export const listCountersRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/counters',
    operationId: 'listCounters',
    tags: ['shifts'],
    summary: 'The venue’s counters and the drawers running at them (ADR-0032)',
    description: 'The counters a shift starts at, each free or with its running drawer, and any drawer open at no counter. A ' +
        'drawer’s money (float, cash so far, expected drawer, per-person breakdown) is shown for a drawer the caller is on, ' +
        'and for every drawer with `shifts:read_all`; it is read from the ledger lines the shift’s cash timeline lists. ' +
        '`includeArchived=true` adds archived counters. Requires `shifts:own`.',
    [PERMISSION_EXTENSION]: SHIFTS_OWN,
    [OTHERS_PERMISSION_EXTENSION]: SHIFTS_READ_ALL,
    request: { params: VenueScopeParamsSchema, query: ListCountersQuerySchema },
    responses: {
        200: jsonResponse(CounterBoardSchema, 'The counters, and drawers running at no counter.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createCounterRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/counters',
    operationId: 'createCounter',
    tags: ['shifts'],
    summary: 'Add a counter (ADR-0032)',
    description: 'Adds a counter with an optional default float and Clavian Counter PC (a station of this venue with role `counter`). ' +
        'The name is unique at the venue, ignoring case. Requires `settings:payments`. Audited.',
    [PERMISSION_EXTENSION]: SETTINGS_PAYMENTS,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreateCounterSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(CounterSchema, 'The new counter.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        409: problemResponse('A counter with that name exists here, or the station is not a Counter PC of this venue.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateCounterRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/counters/{counterId}',
    operationId: 'updateCounter',
    tags: ['shifts'],
    summary: 'Rename, archive or restore a counter (ADR-0032)',
    description: 'Renames a counter (a drawer open there takes the new name; closed shifts keep theirs), changes its default float ' +
        'or Counter PC, or archives (`archived: true`) or restores it. A counter with a drawer open is not archived. ' +
        'Requires `settings:payments`. Audited.',
    [PERMISSION_EXTENSION]: SETTINGS_PAYMENTS,
    request: {
        params: CounterParamsSchema,
        body: { content: { 'application/json': { schema: UpdateCounterSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(CounterSchema, 'The counter, changed.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such counter at this venue.', 'not-found'),
        409: problemResponse('Another counter has that name, the station is not a Counter PC here, or a drawer is open at the counter being archived.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=counters.js.map